import React, { useState, useEffect } from 'react';
import { PoDetail, PoQuotation, PoInvoice, PoDelivery, PoJobcard, PoTicket, PoOther } from './poSlice';
import { updatePoDocumentApi } from '../../services/api';
import toast from 'react-hot-toast';

interface EditDocumentModalProps {
  po: PoDetail | null;
  isOpen: boolean;
  onClose: () => void;
  onDocumentUpdated: (updatedPo: PoDetail) => void;
  documentType: 'quotation' | 'invoice' | 'delivery' | 'jobcard' | 'ticket' | 'other' | null;
  documentId: number | null;
}

const EditDocumentModal: React.FC<EditDocumentModalProps> = ({ 
  po, isOpen, onClose, onDocumentUpdated, documentType, documentId 
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [date, setDate] = useState('');
  const [amount, setAmount] = useState('');
  const [vat, setVat] = useState<'' | '0' | '1'>('');
  const [deliveryNumber, setDeliveryNumber] = useState('');
  const [receivedBy, setReceivedBy] = useState('');
  const [jobNumber, setJobNumber] = useState('');
  const [comment, setComment] = useState('');
  const [updating, setUpdating] = useState(false);

  // Load existing document data when modal opens
  useEffect(() => {
    if (!isOpen || !po || !documentType || !documentId) return;

    const loadDocumentData = () => {
      switch (documentType) {
        case 'quotation':
          const quote = po.quotation as PoQuotation;
          if (quote) {
            setInvoiceNumber(quote.quotation_number || '');
            setDate(quote.date || '');
            setAmount(quote.amount?.toString() || '');
            setVat(quote.vat !== undefined ? (quote.vat ? '1' : '0') : '');
          }
          break;
        case 'invoice':
          const invoice = po.invoices?.find((inv: PoInvoice) => inv.id === documentId);
          if (invoice) {
            setInvoiceNumber(invoice.invoice_number || '');
            setDate(invoice.date || '');
            setAmount(typeof invoice.amount === 'string' ? invoice.amount : invoice.amount?.toString() || '');
            setVat(invoice.vat !== undefined ? (invoice.vat ? '1' : '0') : '');
          }
          break;
        case 'delivery':
          const delivery = po.deliverynotes?.find((dn: PoDelivery) => dn.id === documentId);
          if (delivery) {
            setDeliveryNumber(delivery.delivery_number || '');
            setDate(delivery.date || '');
            setReceivedBy(delivery.received_by || '');
          }
          break;
        case 'jobcard':
          const jobcard = po.jobcards?.find((jc: PoJobcard) => jc.id === documentId);
          if (jobcard) {
            setJobNumber(jobcard.job_number || '');
          }
          break;
        case 'ticket':
          const ticket = po.tickets?.find((t: PoTicket) => t.id === documentId);
          if (ticket) {
            setComment(ticket.comment || '');
          }
          break;
        case 'other':
          const other = po.others?.find((o: PoOther) => o.id === documentId);
          if (other) {
            setTitle(other.title || '');
            setDescription(other.description || '');
          }
          break;
      }
    };

    loadDocumentData();
  }, [isOpen, po, documentType, documentId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!po || !documentType || !documentId) {
      toast.error('Missing document information');
      return;
    }

    setUpdating(true);
    const formData = new FormData();
    
    if (file) formData.append('file', file);
    if (title) formData.append('title', title);
    if (description) formData.append('description', description);
    if (invoiceNumber) formData.append('invoice_number', invoiceNumber);
    if (date) formData.append('date', date);
    if (amount) formData.append('amount', amount);
    if (vat) formData.append('vat', vat);
    if (deliveryNumber) formData.append('delivery_number', deliveryNumber);
    if (receivedBy) formData.append('received_by', receivedBy);
    if (jobNumber) formData.append('job_number', jobNumber);
    if (comment) formData.append('comment', comment);

    try {
      const response = await updatePoDocumentApi(po.id, documentType, documentId, formData);
      toast.success('Document updated successfully');
      onDocumentUpdated(response.data);
      handleClose();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update document');
    } finally {
      setUpdating(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setTitle('');
    setDescription('');
    setInvoiceNumber('');
    setDate('');
    setAmount('');
    setVat('');
    setDeliveryNumber('');
    setReceivedBy('');
    setJobNumber('');
    setComment('');
    onClose();
  };

  if (!isOpen || !po || !documentType || !documentId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Edit {documentType.charAt(0).toUpperCase() + documentType.slice(1)} - {po.stores?.[0]?.name || 'N/A'}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* File Upload - Optional for editing */}
          <div>
            <label htmlFor="file" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Replace File (optional):
            </label>
            <input
              id="file"
              type="file"
              onChange={handleFileChange}
              accept={documentType === 'jobcard' || documentType === 'delivery' || documentType === 'ticket' ? 'application/pdf' : '*'}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
            />
          </div>

          {/* Conditional Fields based on document type */}
          {(documentType === 'quotation' || documentType === 'invoice') && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="invoice_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {documentType === 'quotation' ? 'Quotation' : 'Invoice'} Number:
                </label>
                <input
                  id="invoice_number"
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date:
                </label>
                <input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Amount:
                </label>
                <input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="vat" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  VAT Type:
                </label>
                <select
                  id="vat"
                  value={vat}
                  onChange={(e) => setVat(e.target.value as '' | '0' | '1')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                >
                  <option value="">Select...</option>
                  <option value="1">Included</option>
                  <option value="0">Excluded</option>
                </select>
              </div>
            </div>
          )}

          {documentType === 'delivery' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="delivery_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Delivery Number:
                </label>
                <input
                  id="delivery_number"
                  type="text"
                  value={deliveryNumber}
                  onChange={(e) => setDeliveryNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Date:
                </label>
                <input
                  id="date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="received_by" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Received By:
                </label>
                <input
                  id="received_by"
                  type="text"
                  value={receivedBy}
                  onChange={(e) => setReceivedBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}

          {documentType === 'jobcard' && (
            <div>
              <label htmlFor="job_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Job Number:
              </label>
              <input
                id="job_number"
                type="text"
                value={jobNumber}
                onChange={(e) => setJobNumber(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
              />
            </div>
          )}

          {documentType === 'ticket' && (
            <div>
              <label htmlFor="comment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Comment:
              </label>
              <textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                rows={3}
              />
            </div>
          )}

          {documentType === 'other' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title:
                </label>
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description:
                </label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-900 text-gray-900 dark:text-white"
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updating}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updating ? 'Updating...' : 'Update Document'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDocumentModal;
